import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Input, Button } from "../components";
import { useAuth } from "../hooks/useAuth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { startPasswordLogin, completeWithTotp, completeWithPasskey, enrollPasskey } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMethod, setMfaMethod] = useState<"totp" | "recovery_code">("totp");
  const [requiredActions, setRequiredActions] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: string })?.from ?? "/fleet";

  const phase = mfaChallengeId ? "mfa" : "credentials";

  const parsedQueryChallenge = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const challenge = params.get("loginChallenge");
    const methods = params.get("methods")?.split(",").filter(Boolean) ?? [];
    return { challenge, methods };
  }, [location.search]);

  useEffect(() => {
    if (parsedQueryChallenge.challenge) {
      setMfaChallengeId(parsedQueryChallenge.challenge);
      setMfaMethods(
        parsedQueryChallenge.methods.length > 0 ? parsedQueryChallenge.methods : ["totp"],
      );
    }
  }, [parsedQueryChallenge.challenge, parsedQueryChallenge.methods]);

  async function handlePasswordStart(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await startPasswordLogin(email, password);
      if ("completed" in result && result.completed) {
        setRequiredActions(result.requiredActions);
        navigate(from, { replace: true });
        return;
      }
      setMfaChallengeId(result.challengeId);
      setMfaMethods(result.methods);
      if (result.methods.includes("recovery_code") && !result.methods.includes("totp")) {
        setMfaMethod("recovery_code");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleTotpSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaChallengeId) return;
    setError("");
    setLoading(true);
    try {
      const result = await completeWithTotp(mfaChallengeId, mfaCode, mfaMethod);
      setRequiredActions(result.requiredActions);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskey() {
    if (!mfaChallengeId) return;
    setError("");
    setLoading(true);
    try {
      const result = await completeWithPasskey(mfaChallengeId);
      setRequiredActions(result.requiredActions);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Passkey sign in failed");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskeyEnrollment() {
    if (!mfaChallengeId) return;
    setError("");
    setLoading(true);
    try {
      const result = await enrollPasskey(mfaChallengeId, "Adam passkey");
      setRequiredActions(result.requiredActions);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Passkey enrollment failed");
    } finally {
      setLoading(false);
    }
  }

  function startGoogleLogin() {
    const returnTo = encodeURIComponent("/login");
    window.location.href = `/api/auth/login/google/start?audience=adam&returnTo=${returnTo}`;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--c-surface-2)",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <Card>
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "0.75rem",
                fontSize: "0.65rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "4px 8px",
                borderRadius: 999,
                border: "1px solid var(--c-border)",
                color: "var(--c-text-faint)",
                backgroundColor: "var(--c-surface)",
              }}
            >
              Adam Control Plane
            </div>
            <h1
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: "2rem",
                fontWeight: 400,
                color: "var(--c-text)",
                marginBottom: "0.25rem",
              }}
            >
              Agent Me
            </h1>
            <p style={{ color: "var(--c-text-secondary)", fontSize: "0.875rem" }}>
              {phase === "credentials"
                ? "Sign in with your operator identity"
                : "Complete second-factor verification"}
            </p>
          </div>

          {phase === "credentials" ? (
            <>
              <form onSubmit={handlePasswordStart}>
                <div style={{ marginBottom: "1rem" }}>
                  <Input
                    label="Email or username"
                    type="text"
                    placeholder="admin@agentme.io"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>

                <Button
                  type="submit"
                  variant="accent"
                  disabled={loading || !email || !password}
                  className="w-full"
                >
                  {loading ? "Checking credentials..." : "Continue"}
                </Button>
              </form>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  margin: "1rem 0",
                  color: "var(--c-text-faint)",
                  fontSize: "0.75rem",
                }}
              >
                <div style={{ flex: 1, height: 1, background: "var(--c-border)" }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: "var(--c-border)" }} />
              </div>

              <Button type="button" variant="ghost" onClick={startGoogleLogin} className="w-full">
                Continue with Google
              </Button>
            </>
          ) : (
            <>
              {mfaMethods.includes("passkey_enroll") && (
                <Button
                  type="button"
                  variant="accent"
                  className="w-full"
                  onClick={handlePasskeyEnrollment}
                  disabled={loading}
                >
                  {loading ? "Waiting for passkey..." : "Enroll passkey and continue"}
                </Button>
              )}

              {mfaMethods.includes("passkey") && (
                <Button
                  type="button"
                  variant={mfaMethods.includes("passkey_enroll") ? "ghost" : "accent"}
                  className="w-full"
                  style={
                    mfaMethods.includes("passkey_enroll") ? { marginTop: "0.5rem" } : undefined
                  }
                  onClick={handlePasskey}
                  disabled={loading}
                >
                  {loading ? "Waiting for passkey..." : "Use passkey"}
                </Button>
              )}

              <form
                onSubmit={handleTotpSubmit}
                style={{ marginTop: mfaMethods.includes("passkey") ? "1rem" : 0 }}
              >
                <div
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.5rem",
                    fontSize: "0.75rem",
                  }}
                >
                  {mfaMethods.includes("totp") && (
                    <button
                      type="button"
                      onClick={() => setMfaMethod("totp")}
                      style={{
                        border: "1px solid var(--c-border)",
                        background:
                          mfaMethod === "totp" ? "var(--c-surface-2)" : "var(--c-surface)",
                        borderRadius: 999,
                        padding: "0.25rem 0.6rem",
                      }}
                    >
                      Authenticator code
                    </button>
                  )}
                  {mfaMethods.includes("recovery_code") && (
                    <button
                      type="button"
                      onClick={() => setMfaMethod("recovery_code")}
                      style={{
                        border: "1px solid var(--c-border)",
                        background:
                          mfaMethod === "recovery_code" ? "var(--c-surface-2)" : "var(--c-surface)",
                        borderRadius: 999,
                        padding: "0.25rem 0.6rem",
                      }}
                    >
                      Recovery code
                    </button>
                  )}
                </div>

                <Input
                  label={mfaMethod === "totp" ? "TOTP code" : "Recovery code"}
                  type="text"
                  placeholder={mfaMethod === "totp" ? "123456" : "ABCDE-12345"}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                />

                <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
                  <Button
                    type="submit"
                    variant="accent"
                    disabled={loading || !mfaCode}
                    className="w-full"
                  >
                    {loading ? "Verifying..." : "Verify and sign in"}
                  </Button>
                </div>
              </form>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                style={{ marginTop: "0.75rem" }}
                onClick={() => {
                  setMfaChallengeId(null);
                  setMfaMethods([]);
                  setMfaCode("");
                  setError("");
                }}
                disabled={loading}
              >
                Back
              </Button>
            </>
          )}

          {error && (
            <p
              style={{
                color: "var(--c-danger)",
                fontSize: "0.75rem",
                marginTop: "0.75rem",
                textAlign: "center",
              }}
            >
              {error}
            </p>
          )}

          {requiredActions.length > 0 && (
            <p
              style={{
                color: "var(--c-warning)",
                fontSize: "0.75rem",
                marginTop: "0.75rem",
                textAlign: "center",
              }}
            >
              Required action: {requiredActions.join(", ")}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
